import { useMemo } from "react";
import { TooltipContainer } from "@outplayed/tooltips";
import { asArray } from "@ugg/shared/utils/safe-coerce";
import { getClassicChampionSkillImgUrl, type ClassicChampionData } from "@ugg/shared/hooks/classic";
import { SkillTooltipContent, formatCooldown, SKILL_KEY_LABEL } from "./lib/skill-tooltip";

// Part of the League Classic build page. See ./ClassicChampionProfile.tsx for
// the module boundary rules: nothing outside this folder imports from here
// except ./index.ts, and nothing in here is hoisted into shared component
// directories — that is what keeps Classic deletable as one unit.

export function ClassicSkillsDetail(props: {
  championKey?: string;
  champion?: ClassicChampionData;
  // Resolved <version> for the versioned asset CDN; undefined until the page's
  // patch resolves, in which case the ability icons are skipped.
  assetVersion?: string;
}) {
  const { championKey, champion, assetVersion } = props;

  const entries: Array<{
    key: "q" | "w" | "e" | "r" | "p";
    name?: string;
    description?: string;
    dynamic?: string;
    cooldown?: string | null;
    isPassive: boolean;
  }> = useMemo(() => {
    if (!champion) return [];
    const spellEntries = (["q", "w", "e", "r"] as const).map((key) => {
      const spell = asArray(champion.spells).find((s) => s.spellKey === key);
      return {
        key,
        name: spell?.name,
        description: spell?.description,
        dynamic: spell?.dynamicDescription,
        cooldown: formatCooldown(spell?.cooldownCoefficients),
        isPassive: false,
      };
    });
    return [
      ...spellEntries,
      {
        key: "p" as const,
        name: champion.passive?.name,
        description: champion.passive?.description,
        dynamic: champion.passive?.description,
        cooldown: null,
        isPassive: true,
      },
    ];
  }, [champion]);

  return (
    <div className="content-section">
      <div className="content-section_header">
        <span style={{ marginRight: 12 }}>Skills</span>
        <span className="content-section_blurb">Classic champion abilities</span>
      </div>

      <div className="grid grid-cols-3 gap-[12px] max-md:grid-cols-2 max-sm:grid-cols-1">
        {entries.map(({ key, name, description, dynamic, cooldown, isPassive }) => (
          <div key={key} className="rounded-[3px] bg-[#11112a] p-[16px] font-['Inter']">
            <div className="flex items-center gap-[12px]">
              {/* Icon carries the full tooltip on hover (cooldown + resolved
                  dynamic description), like the header's skill-icon preview. */}
              <TooltipContainer
                disableTooltip={!champion}
                tooltipInfo={
                  <SkillTooltipContent
                    name={name || SKILL_KEY_LABEL[key]}
                    cooldown={cooldown}
                    description={dynamic}
                    isPassive={isPassive}
                  />
                }
              >
                <div className="relative flex-none">
                  {championKey && assetVersion && (
                    <img
                      src={getClassicChampionSkillImgUrl(championKey, key, assetVersion)}
                      alt={name || SKILL_KEY_LABEL[key]}
                      loading="lazy"
                      className="w-[36px] h-[36px] rounded-[3px]"
                    />
                  )}
                  {/* Keybind badge — same overlay the champion header and Skill
                      Priority row use (bottom-center, round), so every skill
                      icon on the page reads identically. */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 flex items-center justify-center w-[12px] h-[12px] rounded-full border-[2px] border-[#17172e] bg-[#383864] text-[9px] font-bold leading-none">
                    {SKILL_KEY_LABEL[key]}
                  </div>
                </div>
              </TooltipContainer>
              {/* Title + description sized to match the Rune Summary module
                  (category name 14px bold; effect/stat text 11px/15px) so this
                  text-heavy card doesn't stand out from the rest of the page. */}
              <div className="text-[14px] font-bold text-white">{name}</div>
            </div>
            <div className="mt-[18px] text-[11px] leading-[15px] text-lavender-50">{description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Rune summary (compact selected-rune cards, grouped by rune type) ─────────

// ─── Mastery summary (compact selected-mastery cards + tooltips) ──────────────

// Highlight the value at the current rank within a "a/b/c" run in a mastery
// description (e.g. "+1.5/3/4.5/6 Health per level" at rank 4 → bold "6").
